import styled from '@emotion/styled'
import { Primary450, Primary500, Primary100 } from '@mehilainen/design-system-tokens/colors'
import { AngleRight } from '@mehilainen/hive-customer/icons'
import { Button } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DCNotification } from '../../../__generated__/api'
import { useIsMobile, useIsTablet } from '../../hooks/useBreakpoint'
import { scale } from '../../utils/scale'
import { PrimaryHover } from '../DefaultButton/DefaultButton'
import { ColumnFlex, RowFlex } from '../Layout/Layout'
import { Text } from '../Typography/Typography'

import BaseTile from './BaseTile'
import { ThreeColumnGrid } from './TimeslotTile'

const Container = styled(RowFlex)<{ isMobile: boolean }>`
  gap: ${scale(2)};
`

const ExternalIcon = styled.div<{ url: string; isMobile: boolean; isTablet: boolean }>`
  background: url(${({ url }) => url}) no-repeat 50% 50%;
  background-size: ${(props) => (props.isMobile || props.isTablet ? '24px' : '36px')};
  background-color: ${Primary100};
  border-radius: 10px;
  min-width: ${(props) => (props.isMobile ? '40px' : props.isTablet ? '50px' : '64px')};
  height: ${(props) => (props.isMobile ? '50px' : props.isTablet ? '70px' : '84px')};
`
const QueueLength = styled(ColumnFlex)`
  justify-content: center;
`

const MobileBaseTile = styled(BaseTile)`
  gap: ${scale(1.5)};

  ${QueueLength} {
    min-width: 80px;
    flex-basis: 25%;
  }

  ${Container} {
    flex-basis: 80%;
  }
`

const DesktopBaseTile = styled(BaseTile)`
  ${() => ThreeColumnGrid}

  &:focus-within {
    border-color: ${Primary450};
  }
`

const TextContainer = styled(ColumnFlex)`
  gap: ${scale(0.75)};
  justify-content: center;
`
const StyledButton = styled(Button)`
  align-self: flex-start;

  ${PrimaryHover}
`

const NoWrap = styled(Text)`
  white-space: nowrap;
`

interface Props {
  notification: DCNotification
  onClick(queueId: string): void
}

const DigitalClinicTile: React.FC<Props> = ({ notification, onClick }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  if (isMobile) {
    return (
      <MobileBaseTile
        onClick={() => onClick(notification.queueId)}
        onKeyPress={(event) => event.key === 'Enter' && onClick(notification.queueId)}
      >
        <QueueLength>
          <Text $size={300} $color={Primary500}>
            {t('component.digitalClinicTile.queueTime')}
          </Text>
          <NoWrap $size={450} $color={Primary500}>
            {`${notification.queueLength} min`}
          </NoWrap>
        </QueueLength>
        <Container isMobile={true}>
          <ExternalIcon
            role="presentation"
            url={`${process.env.REACT_APP_NODE_ICON_URL}${notification.icon}.svg`}
            isMobile={true}
            isTablet={false}
          />
          <TextContainer>
            <Text $size={300} $height="Medium" $weight="Medium">
              {notification.title}
            </Text>
            <Text $size={200} $height="Medium" $weight="Regular">
              {notification.content}
            </Text>
          </TextContainer>
        </Container>

        <AngleRight htmlColor={Primary500} fontSize="large" />
      </MobileBaseTile>
    )
  }
  return (
    <DesktopBaseTile
      onClick={() => onClick(notification.queueId)}
      onKeyPress={(event) => event.key === 'Enter' && onClick(notification.queueId)}
    >
      <QueueLength style={{ marginLeft: scale(1) }}>
        <Text $size={isTablet ? 300 : 400} $color={Primary500}>
          {t('component.digitalClinicTile.queueTime')}
        </Text>
        <NoWrap $size={isTablet ? 400 : 500} $color={Primary500}>
          {`${notification.queueLength} min`}
        </NoWrap>
      </QueueLength>
      <Container isMobile={false}>
        <ExternalIcon
          role="presentation"
          url={`${process.env.REACT_APP_NODE_ICON_URL}${notification.icon}.svg`}
          isMobile={false}
          isTablet={isTablet}
        />
        <TextContainer>
          <Text $color={Primary500} $height="Medium" $weight="Medium" $size={isTablet ? 300 : 400}>
            {notification.title}
          </Text>
          <Text $size={300}>{notification.content}</Text>
        </TextContainer>
      </Container>

      <StyledButton>{notification.ctaText}</StyledButton>
    </DesktopBaseTile>
  )
}

export default DigitalClinicTile
